import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, XCircle, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-white dark:bg-zinc-950">
        <div className="max-w-md w-full text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="font-display text-3xl font-black tracking-tighter mb-2 text-zinc-950 dark:text-zinc-100">
            Invalid link
          </h2>
          <p className="text-sm text-zinc-500 mb-8">
            This password reset link is invalid. Request a new one.
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 text-sm font-medium underline underline-offset-4"
          >
            Request new reset link
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated! Sign in with your new password.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-white dark:bg-zinc-950">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="font-display text-3xl font-black tracking-tighter mb-2 text-zinc-950 dark:text-zinc-100">
            Password updated.
          </h2>
          <p className="text-sm text-zinc-500 mb-8">
            You can now sign in with your new password.
          </p>
          <button
            onClick={() => navigate("/login")}
            className="bg-black dark:bg-white text-white dark:text-black px-6 py-3 text-sm font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-white dark:bg-zinc-950">
      <div className="max-w-md w-full">
        <div className="font-mono-tight text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">
          set new password
        </div>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-2 text-zinc-950 dark:text-zinc-100">
          Reset password.
        </h2>
        <p className="text-sm text-zinc-500 mb-10">
          Choose a new password for your account.{" "}
          <Link to="/login" className="underline underline-offset-4">
            Sign in instead
          </Link>
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2 text-zinc-900 dark:text-zinc-100">
              New Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-base focus:outline-none focus:border-black dark:focus:border-white transition-colors bg-transparent text-zinc-950 dark:text-zinc-100"
              placeholder="Min 6 characters"
              data-testid="reset-password-input"
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.1em] mb-2 text-zinc-900 dark:text-zinc-100">
              Confirm Password
            </label>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              className="w-full border border-zinc-200 dark:border-zinc-700 px-4 py-3 text-base focus:outline-none focus:border-black dark:focus:border-white transition-colors bg-transparent text-zinc-950 dark:text-zinc-100"
              placeholder="Repeat new password"
              data-testid="reset-password-confirm-input"
            />
          </div>
          {error && (
            <div className="border border-red-500 text-red-600 px-4 py-3 text-sm" data-testid="reset-password-error">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black dark:bg-white text-white dark:text-black py-3.5 text-sm font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50"
            data-testid="reset-password-submit"
          >
            {loading ? "Updating..." : "Update password"}
          </button>
        </form>
      </div>
    </div>
  );
}
